import { createAsyncThunk } from "@reduxjs/toolkit";

import apiRequest from "../../../services/api";
import { setApiValue } from "utils/shared/apiValues";

export const usersOverview = createAsyncThunk(
  "organizations/org_code/usersOverview",
  async (body) => {
    const api = await apiRequest.get(`v1/organizations/org_code/`, {
      headers: { organization: body.userOrganization.org_id },
    });
    return api.data;
  }
);

// Logout api
export const logoutUserApi = createAsyncThunk("auth/logout", async () => {
  const api = await apiRequest.post("v1/logout/");
  return api.data;
});

export const fetchUsers = createAsyncThunk(
  "organizations/users/fetchUsers",
  async (body) => {
    let query = "";
    if (body.page) {
      query = query + `page=${body.page}&`;
    }
    if (body.page_size) {
      query = query + `page_size=${body.page_size}&`;
    }
    if (body.search) {
      query = query + `search=${body.search}&`;
    }
    if (body.ordering) {
      query = query + `ordering=${body.ordering}&`;
    }
    if (body.userStatus) {
      query = query + `is_active=${body.userStatus}&`;
    }
    if (body.region) {
      query = query + `region=${body.region}&`;
    }
    if (body.clinic) {
      query = query + `clinic__name=${body.clinic}&`;
    }
    if (body.role) {
      query = query + `role=${body.role}&`;
    }
    if (body.filter_org) {
      query = query + `organization__name=${body.filter_org}&`;
    }
    if (body.premium) {
      query = query + `premium=${body.premium}&`;
    }
    if (body.joined_groups__title) {
      query = query + `joined_groups__title=${body.joined_groups__title}&`;
    }
    const api = await apiRequest.get(`v1/organizations/users/?${query}`, {
      headers: { organization: body.userOrganization?.org_id },
    });
    return api.data;
  }
);

export const fetchUserFilters = createAsyncThunk(
  "organizations/users/fetchFilters",
  async (body) => {
    const apiFetch = await apiRequest.get(`v1/organizations/users/filters/`, {
      headers: { organization: body.userOrganization.org_id },
    });
    return apiFetch.data;
  }
);

export const fetchJoiningRequestUsers = createAsyncThunk(
  "organizations/joining_request",
  async (body) => {
    let query = "";
    if (body.page) {
      query = query + `page=${body.page}&`;
    }
    if (body.page_size) {
      query = query + `page_size=${body.page_size}&`;
    }

    const joiningRequestList = await apiRequest.get(
      `v1/organizations/joining_request?${query}`,
      { headers: { organization: body.userOrganization.org_id } }
    );
    return joiningRequestList.data;
  }
);

export const changeJoiningRequestStatus = createAsyncThunk(
  "organizations/joining_request/changeStatus",
  async (body) => {
    const api = await apiRequest.post(
      `v1/organizations/joining_request/`,
      {
        user_ids: body.userIds,
        status: body.status,
      },
      { headers: { organization: body.organization } }
    );
    return api.data;
  }
);

export const createNewDoctors = createAsyncThunk(
  "organizations/doctors/createNewDoctor",
  async (body, { rejectWithValue }) => {
    try {
      const api = await apiRequest.post(
        `v1/organizations/doctors/`,
        body.userApiData,
        { headers: { organization: body.userOrganization.org_id } }
      );
      return api;
    } catch (err) {
      if (!err.response) {
        throw err;
      }
      return rejectWithValue(err.response.data);
    }
  }
);

export const getEditDoctorDetails = createAsyncThunk(
  "organizations/doctors/getEditDoctorDetails",
  async (body) => {
    const api = await apiRequest.get(
      `v1/organizations/doctors/${body.user_id}`,
      { headers: { organization: body.userOrganization.org_id } }
    );
    return api.data;
  }
);

export const updateDoctorDetails = createAsyncThunk(
  "organizations/doctors/{user_id}/updateDoctorDetails",
  async (body, { rejectWithValue }) => {
    try {
      const api = await apiRequest.put(
        `v1/organizations/doctors/${body.user_id}/`,
        body.bodyData,
        {
          headers: { organization: body.organization },
        }
      );
      return api;
    } catch (err) {
      if (!err.response) {
        throw err;
      }
      return rejectWithValue(err.response.data);
    }
  }
);

// Compliance reports apis
export const getReminderResponse = createAsyncThunk(
  "users/compliance/getReminderResponse",
  async (body) => {
    const reminderResponse = await apiRequest.post(
      `v1/send_reminder/?reminder_type=${body.reminder_type}`,
      { user_ids: body.user_ids },
      { headers: { organization: body.organization } }
    );
    return reminderResponse.data;
  }
);

export const getUserNotifications = createAsyncThunk(
  "user/getUserNotifications",
  async (body) => {
    const userNotifications = await apiRequest.get(
      `v1/notifications/dashboard/`,
      { headers: { organization: body.userOrganization.org_id } }
    );
    return userNotifications.data;
  }
);

// User groups get
export const fetchUserGroups = createAsyncThunk(
  "user/fetch_user_groups",
  async (body) => {
    let query = "";
    if (body.page) {
      query = query + `page=${body.page}&`;
    }
    if (body.page_size) {
      query = query + `page_size=${body.page_size}&`;
    }
    if (body.search) {
      query = query + `search=${body.search}&`;
    }
    if (body.organization_name) {
      query = query + `organization__name=${body.organization_name}&`;
    }
    if (body.organization__org_id) {
      query = query + `organization__org_id=${body.organization__org_id}`;
    }
    const userGroup = await apiRequest.get(`v2/groups/?${query}`, {
      headers: { organization: body.userOrganization.org_id },
    });
    return userGroup.data;
  }
);

// Create User Group
export const createUserGroup = createAsyncThunk(
  "user/create_user_group",
  async (body, { rejectWithValue }) => {
    try {
      const api = await apiRequest.post(`v2/groups/`, body.newUserGroupData, {
        headers: { organization: body.userOrganization.org_id },
      });
      return api;
    } catch (err) {
      if (!err.response) {
        throw err;
      }
      return rejectWithValue(err.response.data);
    }
  }
);
// User groups update
export const updateUserGroups = createAsyncThunk(
  "user/update_user_groups",
  async (body) => {
    const response = await apiRequest.put(`v2/groups/`, body.newUserGroupData, {
      params: {
        group_id: body.GroupId,
      },
      headers: { organization: body.userOrganization.org_id },
    });
    return response;
  }
);
// User groups detail
export const fetchUserGroupDetail = createAsyncThunk(
  "user/fetch_detail_user_group",
  async (body, { rejectWithValue }) => {
    try {
      const response = await apiRequest.get(`v2/groups/`, {
        params: {
          group_id: body.GroupId,
        },
        headers: { organization: body.userOrganization.org_id },
      });
      return response;
    } catch (err) {
      if (!err.response) {
        throw err;
      }
      return rejectWithValue(err.response.data);
    }
  }
);

// User groups delete
export const deleteUserGroups = createAsyncThunk(
  "user/delete_user_groups",
  async (body) => {
    try {
      const response = await apiRequest.delete(`v2/groups/`, {
        params: {
          group_id: body.moduleId,
        },
        headers: {
          organization: body.userOrganization.org_id,
        },
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  }
);

// Create New Support Ticket
export const createSupportTicket = createAsyncThunk(
  "user/create_support_ticket",
  async (body, { rejectWithValue }) => {
    try {
      const api = await apiRequest.post(
        `v1/support_tickets/`,
        body.newSupportTicketData,
        {
          headers: { organization: body.userOrganization.org_id },
        }
      );
      return api.data;
    } catch (err) {
      if (!err.response) {
        throw err;
      }
      return rejectWithValue(err.response.data);
    }
  }
);

export const listClinics = createAsyncThunk(
  "organizations/clinic/listClinics",
  async (body) => {
    const response = await apiRequest.get(`v1/organization/clinics/`, {
      params: {
        search: body.search,
        page: body.page,
        page_size: body.page_size,
        organization__name: body.filter_org,
        organization__org_id: body.organization__org_id,
      },
      headers: { organization: body.userOrganization.org_id },
    });
    return response.data;
  }
);

export const listRegions = createAsyncThunk(
  "organizations/region/listRegions",
  async (body) => {
    const response = await apiRequest.get(`v1/organization/regions/`, {
      params: { search: body.search },
      headers: { organization: body.userOrganization.org_id },
    });
    return response.data;
  }
);

export const sendUserEmailSchedule = createAsyncThunk(
  "user/sendUserOnboarding",
  async (body) => {
    let query = "";
    if (body.userStatus) {
      query = query + `is_active=${body.userStatus}&`;
    }
    if (body.region) {
      query = query + `region=${body.region}&`;
    }
    if (body.clinic) {
      query = query + `clinic__name=${body.clinic}&`;
    }
    if (body.role) {
      query = query + `role=${body.role}&`;
    }
    if (body.filter_org) {
      query = query + `organization__name=${body.filter_org}&`;
    }
    if (body.premium) {
      query = query + `premium=${body.premium}&`;
    }
    if (body.joined_groups__title) {
      query = query + `joined_groups__title=${body.joined_groups__title}&`;
    }
    const response = await apiRequest.post(
      `v2/schedule/email/?${query}`,
      body.payload,
      {
        headers: { organization: body.userOrganization.org_id },
      }
    );
    return response.data;
  }
);

export const sendInstantUserEmailSchedule = createAsyncThunk(
  "user/sendUserOnboarding",
  async (body) => {
    const response = await apiRequest.post(
      `v2/schedule/email/?instant=true`,
      body.payload,
      {
        headers: { organization: body.userOrganization.org_id },
      }
    );
    return response.data;
  }
);

export const getUserEmailSchedule = createAsyncThunk(
  "user/getUserEmailSchedule",
  async (body) => {
    let query = "";
    if (body.page) {
      query = query + `page=${body.page}&`;
    }
    if (body.page_size) {
      query = query + `page_size=${body.page_size}&`;
    }
    const response = await apiRequest.get(`v2/schedule/email/?${query}`, {
      headers: { organization: body.userOrganization.org_id },
    });
    return response.data;
  }
);
export const getUsersListForSchedule = createAsyncThunk(
  "user/getUsersListForSchedule",
  async (body) => {
    let query = "";
    if (body.page) {
      query = query + `page=${body.page}&`;
    }
    if (body.page_size) {
      query = query + `page_size=${body.page_size}&`;
    }
    if (body.search) {
      query = query + `search=${body.search}&`;
    }
    if (body.ordering) {
      query = query + `ordering=${body.ordering}&`;
    }
    if (body.userStatus) {
      query = query + `is_active=${body.userStatus}&`;
    }
    if (body.region) {
      query = query + `region=${body.region}&`;
    }
    if (body.clinic) {
      query = query + `clinic__name=${body.clinic}&`;
    }
    if (body.role) {
      query = query + `role=${body.role}&`;
    }
    if (body.filter_org) {
      query = query + `organization__name=${body.filter_org}&`;
    }
    if (body.premium) {
      query = query + `premium=${body.premium}&`;
    }
    if (body.joined_groups__title) {
      query = query + `joined_groups__title=${body.joined_groups__title}&`;
    }
    const api = await apiRequest.get(`v1/organizations/users/?${query}`, {
      headers: { organization: body.userOrganization?.org_id },
    });
    return api.data;
  }
);

export const getUserEmailScheduleDetails = createAsyncThunk(
  "user/getUserEmailScheduleDetails",
  async (body) => {
    let query = "";
    if (body.page) {
      query = query + `page=${body.page}&`;
    }
    if (body.page_size) {
      query = query + `page_size=${body.page_size}&`;
    }
    const response = await apiRequest.get(
      `v2/schedule/email/?schedule_uuids=${body.schedule_uuids}&${query}`,
      {
        headers: { organization: body.userOrganization.org_id },
      }
    );
    return response.data;
  }
);

export const deleteUserEmailSchedule = createAsyncThunk(
  "user/deleteUserEmailSchedule",
  async (body) => {
    const response = await apiRequest.delete(`v2/schedule/email/`, {
      headers: { organization: body.userOrganization.org_id },
      data: body.payload,
    });
    return response.data;
  }
);

export const bulkUpdateDoctors = createAsyncThunk(
  "organizations/doctors/bulkUpdateDoctors",
  async (body) => {
    const api = await apiRequest.post(
      `v2/organizations/doctors/bulk_update/`,
      body.payload,
      {
        headers: { organization: body.userOrganization?.org_id },
      }
    );
    return api.data;
  }
);

// Upload user bulk upload file
export const uploadUserBulkUploadFile = createAsyncThunk(
  "users/bulk_upload",
  async (body, { rejectWithValue }) => {
    try {
      const newUserBulkUpload = await apiRequest.post(
        `v2/draft/doctors/bulk-upload/`,
        body.newUserBulkUploadData,
        {
          headers: {
            organization: body.userOrganization?.org_id,
            "content-type": "multipart/form-data",
          },
          onUploadProgress: (progressEvent) => {
            const totalLength = progressEvent.lengthComputable
              ? progressEvent.total
              : progressEvent.target.getResponseHeader("content-length") ||
                progressEvent.target.getResponseHeader(
                  "x-decompressed-content-length"
                );
            const uploadProgress = Math.round(
              (progressEvent.loaded * 100) / totalLength
            );
            if (uploadProgress !== null) {
              setApiValue({
                type: "userUploadWindowProgress",
                data: uploadProgress,
              });
            }
          },
        }
      );
      return newUserBulkUpload.data;
    } catch (err) {
      if (!err.response) {
        throw err;
      }
      return rejectWithValue(err.response.data);
    }
  }
);

//Upload user group upload file
export const uploadUserGroupBulkUploadFile = createAsyncThunk(
  "users/bulk_upload",
  async (body, { rejectWithValue }) => {
    try {
      const newUserGroupBulkUpload = await apiRequest.post(
        `v2/draft/user_groups/bulk-upload/`,
        body.newUserGroupBulkUploadData,
        {
          headers: {
            organization: body.userOrganization?.org_id,
            "content-type": "multipart/form-data",
          },
          onUploadProgress: (progressEvent) => {
            const totalLength = progressEvent.lengthComputable
              ? progressEvent.total
              : progressEvent.target.getResponseHeader("content-length") ||
                progressEvent.target.getResponseHeader(
                  "x-decompressed-content-length"
                );
            const uploadProgress = Math.round(
              (progressEvent.loaded * 100) / totalLength
            );
            if (uploadProgress !== null) {
              setApiValue({
                type: "userGroupUploadWindowProgress",
                data: uploadProgress,
              });
            }
          },
        }
      );
      return newUserGroupBulkUpload.data;
    } catch (err) {
      if (!err.response) {
        throw err;
      }
      return rejectWithValue(err.response.data);
    }
  }
);

// User Bulk upload review
export const fetchUserBulkUploadReview = createAsyncThunk(
  "users/fetch_user_bulk_upload_review",
  async () => {
    const programBulkUploadReview = await apiRequest.get(
      "v2/draft/doctors/bulk-upload/review/"
    );
    return programBulkUploadReview.data;
  }
);

//user group bulk upload review
export const fetchUserGroupBulkUploadReview = createAsyncThunk(
  "users/fetch_user_group_bulk_upload_review",
  async () => {
    const programBulkUploadReview = await apiRequest.get(
      "v2/draft/user_groups/bulk-upload/review/"
    );
    return programBulkUploadReview.data;
  }
);

// User Bulk upload - Delete staged user
export const deleteStagedUser = createAsyncThunk(
  "users/delete_staged_user",
  async (body, { rejectWithValue }) => {
    try {
      const deleteStagedUserApi = await apiRequest.delete(
        `v2/draft/doctors/${body.id}/`
      );
      return deleteStagedUserApi.data;
    } catch (err) {
      if (!err.response) {
        throw err;
      }
      return rejectWithValue(err.response.data);
    }
  }
);

// User Bulk upload - Delete staged user
export const deleteStagedUserGroup = createAsyncThunk(
  "users/delete_staged_user_group",
  async (body, { rejectWithValue }) => {
    try {
      const deleteStagedUserApi = await apiRequest.delete(
        `v2/draft/user_groups/${body.id}/`
      );
      return deleteStagedUserApi.data;
    } catch (err) {
      if (!err.response) {
        throw err;
      }
      return rejectWithValue(err.response.data);
    }
  }
);

// User Bulk upload - Discard staged user
export const discardStagedUser = createAsyncThunk(
  "users/discard_staged_user",
  async (body, { rejectWithValue }) => {
    try {
      const discardStagedUserApi = await apiRequest.delete(
        "v2/draft/doctors/bulk-upload/"
      );
      return discardStagedUserApi.data;
    } catch (err) {
      if (!err.response) {
        throw err;
      }
      return rejectWithValue(err.response.data);
    }
  }
);

export const discardStagedUserGroup = createAsyncThunk(
  "users/discard_staged_user_group",
  async (body, { rejectWithValue }) => {
    try {
      const discardStagedUserApi = await apiRequest.delete(
        "v2/draft/user_groups/bulk-upload/"
      );
      return discardStagedUserApi.data;
    } catch (err) {
      if (!err.response) {
        throw err;
      }
      return rejectWithValue(err.response.data);
    }
  }
);

// User Bulk upload - publish staged users
export const publishStagedUser = createAsyncThunk(
  "users/publish_staged_user",
  async (body, { rejectWithValue }) => {
    try {
      const publishUserData = await apiRequest.post(
        `v2/draft/doctors/publish/`
      );
      return publishUserData;
    } catch (err) {
      if (!err.response) {
        throw err;
      }
      return rejectWithValue(err.response.data);
    }
  }
);

export const publishStagedUserGroup = createAsyncThunk(
  "users/publish_staged_user_group",
  async (body, { rejectWithValue }) => {
    try {
      const publishUserData = await apiRequest.post(
        `v2/draft/user_groups/publish/`
      );
      return publishUserData;
    } catch (err) {
      if (!err.response) {
        throw err;
      }
      return rejectWithValue(err.response.data);
    }
  }
);

export const exportUserGroup = createAsyncThunk(
  "users/export_user_group",
  async (body, { rejectWithValue }) => {
    try {
      const exportUserData = await apiRequest.post(`v2/user_groups/export/`, {
        group_ids: body.group_ids,
      });
      return exportUserData;
    } catch (err) {
      if (!err.response) {
        throw err;
      }
      return rejectWithValue(err.response.data);
    }
  }
);

// User Bulk upload review - Edit draft user
export const fetchBulkUploadReviewUser = createAsyncThunk(
  "users/fetch_bulk_upload_review_user",
  async (body) => {
    const bulkUploadReviewUser = await apiRequest.get(
      `v2/draft/doctors/${body.Id}/`
    );
    return bulkUploadReviewUser.data;
  }
);

export const fetchBulkUploadReviewUserGroup = createAsyncThunk(
  "users/fetch_bulk_upload_review_user_group",
  async (body) => {
    const bulkUploadReviewUserGroup = await apiRequest.get(
      `v2/draft/user_groups/${body.Id}/`
    );
    return bulkUploadReviewUserGroup.data;
  }
);

// edit user in user group
export const editUserInUserGroup = createAsyncThunk(
  "users/edit_user_in_user_group",
  async (body, { rejectWithValue }) => {
    try {
      const { Id, ...updatedUserData } = body;
      const editUserInUserGroup = await apiRequest.put(
        `v2/draft/user_groups/${Id}/`,
        updatedUserData
      );
      return editUserInUserGroup.data;
    } catch (error) {
      // Extract and return the API error response
      if (error.response && error.response.data) {
        return rejectWithValue(error.response.data);
      }
      // Fallback for other errors
      return rejectWithValue({
        message: error.message || "Something went wrong",
      });
    }
  }
);

//User Bulk upload review - Delete draft user
export const deleteBulkUploadReviewUser = createAsyncThunk(
  "users/delete_bulk_upload_review_user",
  async (body, { rejectWithValue }) => {
    try {
      const deleteStagedUserApi = await apiRequest.delete(
        `v2/draft/doctors/${body.id}/`
      );
      return deleteStagedUserApi.data;
    } catch (err) {
      if (!err.response) {
        throw err;
      }
      return rejectWithValue(err.response.data);
    }
  }
);

// User Bulk upload - update draft Doctor/User
export const updateStagedUser = createAsyncThunk(
  "users/update_staged_user",
  async (body, { rejectWithValue }) => {
    try {
      const updateData = await apiRequest.put(
        `v2/draft/doctors/${body.Id}/`,
        body.updatedUserData
      );
      return updateData;
    } catch (err) {
      if (!err.response) {
        throw err;
      }
      return rejectWithValue(err.response.data);
    }
  }
);

// User bulk Export
export const userBulkExport = createAsyncThunk(
  "user/bulk_export",
  async (body) => {
    let query = "";
    if (body.userStatus) {
      query = query + `is_active=${body.userStatus}&`;
    }
    if (body.region) {
      query = query + `region=${body.region}&`;
    }
    if (body.clinic) {
      query = query + `clinic__name=${body.clinic}&`;
    }
    if (body.role) {
      query = query + `role=${body.role}&`;
    }
    if (body.filter_org) {
      query = query + `organization__name=${body.filter_org}&`;
    }
    if (body.premium) {
      query = query + `premium=${body.premium}&`;
    }
    if (body.joined_groups__title) {
      query = query + `joined_groups__title=${body.joined_groups__title}&`;
    }
    const response = await apiRequest.post(
      `v2/doctors/export/?${query}`,
      body.payload,
      {
        headers: { organization: body.userOrganization.org_id },
      }
    );
    return response.data;
  }
);

export const orgEnabledSensors = createAsyncThunk(
  "organizations/orgEnabledSensors",
  async (body) => {
    const api = await apiRequest.get(`sensor/organization/config`, {
      headers: { organization: body.userOrganization.org_id },
    });
    return api.data.enabled_sensors;
  }
);
